import React, { useEffect, useRef, useState } from "react";
import { HiddenVisually, Icon, Image, Popover, Stack, Text, useTheme } from "@bookingcom/bui-react";
import { InfoSignIcon } from "@bookingcom/bui-assets-react/streamline";
import styles from "./GeniusAirlinesBanner.module.css";
import { UIGeniusCampaignBanner } from "@flights/types";
import { Trans, useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import useGeniusAirlinesBannerEvent from "../../../hooks/c360/useGeniusAirlinesBannerEvent";
import useIsInViewport from "../../../hooks/useIsInViewport";
import useGeniusAirlinesBanner from "../../../utils/experiments/genius/web_flights_lve_index_genius_airlines_banner";

interface Props {
  geniusAirlineBannerData: UIGeniusCampaignBanner;
  isSecondaryOffer: boolean;
}

const GeniusAirlinesBanner: React.FC<Props> = (props) => {
  const { geniusAirlineBannerData, isSecondaryOffer } = props;
  const airlines = geniusAirlineBannerData.airlines;
  const theme = useTheme();
  const i18n = useI18n();

  const trackGeniusAirlinesBannerEvent = useGeniusAirlinesBannerEvent();
  useEffect(() => {
    if (isFeatureRunningClientSide("FLIGHTS_LVE_ENABLE_SUBMIT_C360_EVENTS_FOR_GENIUS_AIRLINES_BANNER")) {
      trackGeniusAirlinesBannerEvent(airlines.map((airline) => airline.iataCode));
    }
  }, [trackGeniusAirlinesBannerEvent, airlines]);

  // Start - web_flights_lve_index_genius_airlines_banner
  const geniusAirlinesBanner = useGeniusAirlinesBanner();
  const carriersRef = useRef<HTMLElement>(null);
  const isInViewport = useIsInViewport(carriersRef.current);

  useEffect(() => {
    const trackExperimentRender = () => {
      geniusAirlinesBanner.trackCustomGoalWithValueBannerRendered();
    };
    window.addEventListener("load", trackExperimentRender);
    return () => window.removeEventListener("load", trackExperimentRender);
  }, [geniusAirlinesBanner]);

  const [alreadyTrackedViewed, setAlreadyTrackedViewed] = useState(false);
  useEffect(() => {
    // Only tracked view once per render. Other wise scroll up/down on the page would trigger the view multiple times
    if (isInViewport && !alreadyTrackedViewed) {
      geniusAirlinesBanner.trackCustomGoalWithValueBannerViewed();
      setAlreadyTrackedViewed(true);
    }
  }, [isInViewport, geniusAirlinesBanner, alreadyTrackedViewed]);
  // End - web_flights_lve_index_genius_airlines_banner

  const tooltipLabel = i18n.trans(t("flights_lve_airline_promotions_genius_tooltip"));

  // Responsiveness: check small viewport to remove the genius side image
  const [isExtraSmall, setIsExtraSmall] = useState(false);
  useEffect(() => {
    const checkWidth = () => {
      setIsExtraSmall(window.innerWidth < 360);
    };
    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  const carriersList = (
    <Stack
      direction="row"
      wrap="wrap"
      className={styles.carriersListWrapper}
      justifyContent={isSecondaryOffer ? "start" : "end"}
      gap={isSecondaryOffer ? 2.3 : 3}
      attributes={{ role: "list", ref: carriersRef }}
    >
      {airlines.map(
        ({ name, imageUrl }) =>
          !!name &&
          !!imageUrl && (
            <Stack.Item
              key={name}
              className={
                !isSecondaryOffer
                  ? styles.airlinesLogoWrapperAsPrimaryOffer
                  : styles.airlinesLogoWrapperAsSecondaryOffer
              }
              attributes={{ role: "listitem" }}
            >
              <Stack direction="column" justifyContent="space-between" alignItems="center" gap={2}>
                <Image width="32px" src={imageUrl} aria-hidden="true" alt="" />
                <Text variant="small_2" wrap="balance" maxLines={1} bidirectional>
                  {name}
                </Text>
              </Stack>
            </Stack.Item>
          )
      )}
    </Stack>
  );

  const popoverComponent = (
    <Popover
      position="top-start"
      triggerType="hover"
      navigationMode="tab"
      hideClose={true}
      keepMounted={true}
      onOpen={() => {
        geniusAirlinesBanner.trackCustomGoalBannerHovered();
      }}
    >
      <Popover.Trigger>
        {(attrs) => (
          <span
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            {...attrs}
            role="tooltip"
            aria-label={tooltipLabel}
          >
            <Icon
              svg={InfoSignIcon}
              size="smallest"
              color="neutral_alt"
              className={styles.tooltip__icon__wrapper}
              attributes={{ "aria-hidden": true }}
            />
          </span>
        )}
      </Popover.Trigger>
      <Popover.Content
        attributes={{
          "aria-label": tooltipLabel,
          id: "genius_airlines_tooltip",
          role: "tooltip"
        }}
      >
        <Text variant="body_1">{tooltipLabel}</Text>
      </Popover.Content>
    </Popover>
  );

  // Handle banner click via attributes
  const handleBannerClick = () => {
    geniusAirlinesBanner.trackCustomGoalBannerClicked();
  };

  return (
    <Stack
      direction={!isSecondaryOffer ? "row" : "column"}
      gap={3}
      className={styles.container}
      alignItems={!isSecondaryOffer ? "center" : "start"}
      justifyContent="space-between"
      attributes={{ onClick: handleBannerClick }}
    >
      <Stack.Item>
        <Stack direction={"row"} justifyContent={"start"} gap={4} alignItems="center">
          {!isSecondaryOffer && !isExtraSmall && (
            <Stack.Item>
              <Image
                height="64px"
                width="64px"
                contentMode="fit"
                asset={{
                  setName: "illustrations-traveller",
                  assetName: "GeniusFlightsBadge"
                }}
              />
            </Stack.Item>
          )}
          <Stack.Item className={styles.bannerText}>
            <Stack direction="row" gap={2} alignItems="center">
              <Text variant="strong_1">
                <HiddenVisually tagName={"h3"}>
                  <Trans
                    aria-hidden
                    tag="flights_index_genius_airline_banner_title"
                    variables={{
                      start_styling: "",
                      end_styling: ""
                    }}
                  />
                </HiddenVisually>
                <div aria-hidden>
                  <Trans
                    tag="flights_index_genius_airline_banner_title"
                    variables={{
                      start_styling: "<0>",
                      end_styling: "</0>"
                    }}
                    components={[<span key={0} style={{ color: theme.colors.color_brand_primary_background }} />]}
                  />
                </div>
              </Text>
              {popoverComponent}
            </Stack>
            <Text variant="body_2" className={isSecondaryOffer ? styles.subtitle : styles.subtitleWithMaxWidth}>
              <Trans tag="flights_index_genius_airline_banner_body" />
            </Text>
          </Stack.Item>
        </Stack>
      </Stack.Item>
      <Stack.Item>{carriersList}</Stack.Item>
    </Stack>
  );
};

export default GeniusAirlinesBanner;
